import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { t } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import gt from 'ember-truth-helpers/helpers/gt';
import lowerCase from 'lodash/lowerCase';
import moment from 'moment-timezone';
import BvEmptySearchResult from 'volta/components/bv-empty-search-result';
import BvText from 'volta/components/bv-text';
import TasksKitList, {
  getEntityFromGroupId,
  getEntityIdFromGroupId
} from 'volta/components/tasks-kit/list';
import ForecastProjectPayload from 'volta/components/tasks-kit/list/forecast-project-payload';
import PoPayload from 'volta/components/tasks-kit/list/po-payload';
import SkuPayload from 'volta/components/tasks-kit/list/sku-payload';
import TaskService from 'volta/services/task-service';
import { localizedFormatMD } from 'volta/utils/date-utils';

import type { IntlService } from 'ember-intl';
import type { BvTextSignature } from 'volta/components/bv-text';
import type { TCommonListArgs } from 'volta/components/tasks-kit/list';
import type { TEntityType } from 'volta/models/types/entity-types';
import type { UTask } from 'volta/models/types/tasks';

const getDueDate = (formattedLabel: string) => {
  const split = formattedLabel.split('|');
  return split[1] ? localizedFormatMD(moment.utc(split[0], 'DD/MM/YYYY')) : undefined;
};

interface TasksKitListDateEntitySignature {
  Args: Omit<TCommonListArgs, 'tasks'> & {
    entities: Record<string, { linkedEntityType: TEntityType }>;
    showTaskType?: boolean;
    onLightBg?: boolean;
    tasks: UTask[];
  };
}

interface Section {
  label: string;
  labelColor: BvTextSignature['Args']['color'];
  tasks: Record<string, UTask[]>;
}

export default class TasksKitListDateEntityComponent extends Component<TasksKitListDateEntitySignature> {
  @service taskService!: TaskService;
  @service intl!: IntlService;

  noDueDateId = this.intl.t('tasksPage.noDueDate');

  @cached
  get dueTasks() {
    return this.args.tasks.filter((t) => !!t.dueDate);
  }

  @cached
  get sections(): Section[] {
    const { indexTasksByDateAndEntity } = TaskService;
    const today = new Date();

    return [
      {
        label: 'overdue',
        labelColor: 'critical',
        tasks: indexTasksByDateAndEntity(
          this.dueTasks.filter((t) => moment.utc(t.dueDate).isBefore(today, 'day')),
          this.noDueDateId
        )
      },
      {
        label: 'today',
        labelColor: 'warning',
        tasks: indexTasksByDateAndEntity(
          this.dueTasks.filter((t) => moment.utc(t.dueDate).isSame(today, 'day')),
          this.noDueDateId
        )
      },
      {
        label: 'later',
        labelColor: 'success',
        tasks: indexTasksByDateAndEntity(
          this.dueTasks.filter((t) => moment.utc(t.dueDate).isAfter(today, 'day')),
          this.noDueDateId
        )
      }
    ].filter((section) => Object.keys(section.tasks).length > 0) as Section[];
  }

  <template>
    {{#each this.sections key='label' as |section|}}
      <div>
        <div class='bv-task-modular__section-header'>
          <BvText @variant='heading-lg' @color={{section.labelColor}} @text={{t section.label}} />
        </div>
        <TasksKitList
          @isLoading={{@isLoading}}
          @entityResourceName={{@entityResourceName}}
          @dueDateIdPrefix={{@dueDateIdPrefix}}
          @tasks={{section.tasks}}
          @selectedTask={{@selectedTask}}
          @onSubmit={{@onSubmit}}
          @onResolve={{@onResolve}}
          @onDelete={{@onDelete}}
          @onDraft={{@onDraft}}
          @onSelect={{@onSelect}}
          @isAutoResolvable={{@isAutoResolvable}}
          @selectedView={{@selectedView}}
          @taskType={{@taskType}}
          @newDisabled={{@newDisabled}}
          @showTaskType={{true}}
          @readonlyTasks={{@readonlyTasks}}
          @authorizedUsers={{@authorizedUsers}}
          @hideAddButton={{@hideAddButton}}
          @onLightBg={{@onLightBg}}
          @taskWrapperClass='u-flex-1'
          class='u-py-1 u-d-flex'
        >
          <:default as |groupId|>
            <div class='u-mr-2 u-pt-2 bv-task-modular__date'>
              {{#if (getDueDate groupId)}}
                <BvText
                  @variant='heading-sm'
                  @text={{getDueDate groupId}}
                  class='u-text-upper u-py-1'
                />
              {{/if}}
            </div>
          </:default>
          <:taskGroupHeader as |groupId _tasks index|>
            {{#let
              (getEntityIdFromGroupId groupId) (getEntityFromGroupId @entities groupId)
              as |entityId entity|
            }}
              {{#if entity}}
                <div class={{if (gt index 0) 'u-border-bottom-divider u-pb-2'}}>
                  {{#if (eq entity.linkedEntityType 'skus')}}
                    {{! @glint-ignore }}
                    <SkuPayload @payload={{entity}} @id={{entityId}} />
                  {{else if (eq entity.linkedEntityType 'planningOrders')}}
                    {{! @glint-ignore }}
                    <PoPayload @payload={{entity}} @id={{entityId}} />
                  {{else if (eq entity.linkedEntityType 'forecastProjects')}}
                    {{! @glint-ignore }}
                    <ForecastProjectPayload @payload={{entity}} @id={{entityId}} />
                  {{/if}}
                </div>
              {{/if}}
            {{/let}}
          </:taskGroupHeader>
        </TasksKitList>
      </div>
    {{else}}
      <BvEmptySearchResult
        class='u-p-5'
        @title={{t 'bvList.emptySearchResultTitle' resourceNamePlural=(lowerCase (t 'tasks'))}}
      />
    {{/each}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'TasksKit::List::DateEntity': typeof TasksKitListDateEntityComponent;
    'tasks-kit/list/date-entity': typeof TasksKitListDateEntityComponent;
  }
}
